import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Button, ListGroup, Badge, Form, InputGroup, Tabs, Tab, Table, Pagination } from 'react-bootstrap';
import { Users, BookCopy, ChevronRight, Zap, Search, Trash2 } from 'lucide-react';
import api from '../../api/api';
import { showToast } from '../../utils/toast';

const BulkFacultyManager = ({ slug, allFaculty, allProjects, onComplete }) => {
    const [activeTab, setActiveTab] = useState('assign');
    const [isSaving, setIsSaving] = useState(false);

    // --- ASSIGNMENT TAB STATE ---
    const [selectedFaculty, setSelectedFaculty] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [facultySearch, setFacultySearch] = useState('');
    const [projectSearch, setProjectSearch] = useState('');
    const [facultyPage, setFacultyPage] = useState(1);
    const [projectPage, setProjectPage] = useState(1);
    const itemsPerPage = 10;

    // --- MANAGE TAB STATE ---
    const [activeAssignments, setActiveAssignments] = useState({});
    const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);

    // Fetch active assignments when switching to the Manage tab
    useEffect(() => {
        if (activeTab === 'manage') {
            loadAssignments();
        }
    }, [activeTab]);

    const loadAssignments = async () => {
        setIsLoadingAssignments(true);
        try {
            const res = await api.get(`/tenant/${slug}/admin/faculty-assignments`);
            setActiveAssignments(res.data.assignments || {});
        } catch (err) {
            console.error("Failed to load assignments", err);
        } finally {
            setIsLoadingAssignments(false);
        }
    };

    // --- FILTER & PAGINATION LOGIC ---
    const filteredFaculty = useMemo(() => {
        return allFaculty.filter(f => 
            f.name.toLowerCase().includes(facultySearch.toLowerCase()) || 
            f.email.toLowerCase().includes(facultySearch.toLowerCase())
        );
    }, [allFaculty, facultySearch]);

    const paginatedFaculty = useMemo(() => {
        const start = (facultyPage - 1) * itemsPerPage;
        return filteredFaculty.slice(start, start + itemsPerPage);
    }, [filteredFaculty, facultyPage]);

    const filteredProjects = useMemo(() => {
        return allProjects.filter(p => p.name.toLowerCase().includes(projectSearch.toLowerCase()));
    }, [allProjects, projectSearch]);

    const paginatedProjects = useMemo(() => {
        const start = (projectPage - 1) * itemsPerPage;
        return filteredProjects.slice(start, start + itemsPerPage);
    }, [filteredProjects, projectPage]);

    // --- ACTIONS ---
    const toggleItem = (id, list, setList) => {
        setList(list.includes(id) ? list.filter(i => i !== id) : [...list, id]);
    };

    const handleBulkSave = async () => {
        if (selectedFaculty.length === 0 || selectedProjects.length === 0) {
            showToast("Please select at least one faculty and one project.", 'warning');
            return;
        }
        setIsSaving(true);
        try {
            // Send emails to match your updated backend architecture
            const facultyEmails = allFaculty.filter(f => selectedFaculty.includes(f.id)).map(f => f.email);
            
            await api.post(`/tenant/${slug}/admin/assign-faculty`, {
                faculty_emails: facultyEmails,
                project_ids: selectedProjects
            });
            
            setSelectedFaculty([]);
            setSelectedProjects([]);
            if (onComplete) onComplete();
            showToast("Faculty mapping updated successfully.", 'success');
        } catch (err) {
            console.error("Mapping failed", err);
            showToast("Failed to update mapping.", 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleUnassign = async (projectId, userEmail) => {
        if (!window.confirm(`Remove ${userEmail} from this course?`)) return;
        try {
            await api.post(`/tenant/${slug}/admin/unassign-faculty`, {
                project_id: projectId,
                faculty_email: userEmail
            });
            loadAssignments(); // Refresh list
        } catch (err) {
            showToast("Failed to unassign faculty.", 'error');
        }
    };

    return (
        <Card className="shadow-sm border-0">
            <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-bold">Faculty Course Mapping</h5>
                <Badge bg="soft-info" className="text-info border border-info">Tenant: {slug}</Badge>
            </Card.Header>
            <Card.Body>
                <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
                    
                    {/* --- ASSIGNMENT TAB --- */}
                    <Tab eventKey="assign" title="Assign Courses">
                        <Row>
                            {/* Faculty Selection */}
                            <Col md={5}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="small fw-bold text-muted mb-0 text-uppercase ls-1">1. Select Faculty</h6>
                                    <Badge bg="secondary">{selectedFaculty.length} Selected</Badge>
                                </div>
                                <InputGroup size="sm" className="mb-2">
                                    <InputGroup.Text className="bg-white"><Search size={14} /></InputGroup.Text>
                                    <Form.Control 
                                        placeholder="Search by name or email..." 
                                        value={facultySearch} 
                                        onChange={(e) => { setFacultySearch(e.target.value); setFacultyPage(1); }}
                                    />
                                </InputGroup>
                                <ListGroup className="border rounded mb-2" style={{ height: '350px', overflowY: 'auto' }}>
                                    {paginatedFaculty.map(f => (
                                        <ListGroup.Item 
                                            key={f.id} active={selectedFaculty.includes(f.id)}
                                            onClick={() => toggleItem(f.id, selectedFaculty, setSelectedFaculty)}
                                            className="cursor-pointer d-flex justify-content-between align-items-center border-0 border-bottom"
                                        >
                                            <div>
                                                <div className="fw-medium">{f.name}</div>
                                                <div className="x-small text-muted">{f.email}</div>
                                            </div>
                                            {selectedFaculty.includes(f.id) && <Users size={16} />}
                                        </ListGroup.Item>
                                    ))}
                                    {paginatedFaculty.length === 0 && <div className="p-3 text-center text-muted small">No faculty found.</div>}
                                </ListGroup>
                                {/* Faculty Pagination */}
                                <div className="d-flex justify-content-between align-items-center">
                                    <small className="text-muted">Total: {filteredFaculty.length}</small>
                                    <Pagination size="sm" className="mb-0">
                                        <Pagination.Prev disabled={facultyPage === 1} onClick={() => setFacultyPage(p => p - 1)} />
                                        <Pagination.Next disabled={facultyPage * itemsPerPage >= filteredFaculty.length} onClick={() => setFacultyPage(p => p + 1)} />
                                    </Pagination>
                                </div>
                            </Col>

                            <Col md={2} className="d-flex align-items-center justify-content-center">
                                <ChevronRight className="text-muted opacity-25" size={48} />
                            </Col>

                            {/* Project Selection */}
                            <Col md={5}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="small fw-bold text-muted mb-0 text-uppercase ls-1">2. Select Projects</h6>
                                    <Badge bg="secondary">{selectedProjects.length} Selected</Badge>
                                </div>
                                <InputGroup size="sm" className="mb-2">
                                    <InputGroup.Text className="bg-white"><Search size={14} /></InputGroup.Text>
                                    <Form.Control 
                                        placeholder="Search courses..." 
                                        value={projectSearch} 
                                        onChange={(e) => { setProjectSearch(e.target.value); setProjectPage(1); }}
                                    />
                                </InputGroup>
                                <ListGroup className="border rounded mb-2" style={{ height: '350px', overflowY: 'auto' }}>
                                    {paginatedProjects.map(p => (
                                        <ListGroup.Item 
                                            key={p.id} active={selectedProjects.includes(p.id)}
                                            onClick={() => toggleItem(p.id, selectedProjects, setSelectedProjects)}
                                            className="cursor-pointer d-flex justify-content-between align-items-center border-0 border-bottom"
                                        >
                                            <div className="flex-grow-1">
                                                <div className="small fw-bold">{p.name}</div>
                                                <Badge bg={p.status === 'completed' ? 'success' : 'warning'} className="x-small-badge mt-1">
                                                    {p.status === 'completed' ? 'CKB Ready' : 'Indexing'}
                                                </Badge>
                                            </div>
                                            {selectedProjects.includes(p.id) && <BookCopy size={16} />}
                                        </ListGroup.Item>
                                    ))}
                                    {paginatedProjects.length === 0 && <div className="p-3 text-center text-muted small">No courses found.</div>}
                                </ListGroup>
                                {/* Project Pagination */}
                                <div className="d-flex justify-content-between align-items-center">
                                    <small className="text-muted">Total: {filteredProjects.length}</small>
                                    <Pagination size="sm" className="mb-0">
                                        <Pagination.Prev disabled={projectPage === 1} onClick={() => setProjectPage(p => p - 1)} />
                                        <Pagination.Next disabled={projectPage * itemsPerPage >= filteredProjects.length} onClick={() => setProjectPage(p => p + 1)} />
                                    </Pagination>
                                </div>
                            </Col>
                        </Row>
                        <div className="mt-4 pt-3 border-top d-flex justify-content-end">
                            <Button variant="primary" className="fw-bold px-4 d-flex align-items-center gap-2" onClick={handleBulkSave} disabled={isSaving}>
                                {isSaving ? 'Processing...' : <><Zap size={16} /> Execute Mapping</>}
                            </Button>
                        </div>
                    </Tab>

                    {/* --- MANAGE ACTIVE ASSIGNMENTS TAB --- */}
                    <Tab eventKey="manage" title="Manage Active Assignments">
                        {isLoadingAssignments ? (
                            <div className="text-center py-5 text-muted">Loading assignments...</div>
                        ) : Object.keys(activeAssignments).length === 0 ? (
                            <div className="text-center py-5 text-muted border rounded">No faculty assigned to any courses yet.</div>
                        ) : (
                            <div className="overflow-auto border rounded" style={{ maxHeight: '500px' }}>
                                <Table hover responsive className="mb-0 align-middle">
                                    <thead className="bg-light x-small text-uppercase text-muted sticky-top">
                                        <tr><th>Course</th><th>Assigned Faculty</th><th>Action</th></tr>
                                    </thead>
                                    <tbody className="small">
                                        {Object.entries(activeAssignments).map(([projectId, emails]) => {
                                            const project = allProjects.find(p => String(p.id) === String(projectId));
                                            return emails.map((email, idx) => (
                                                <tr key={`${projectId}-${email}`}>
                                                    {idx === 0 ? (
                                                        <td rowSpan={emails.length} className="fw-bold bg-white border-end" style={{ width: '40%' }}>
                                                            {project?.name || `Project ID: ${projectId}`}
                                                        </td>
                                                    ) : null}
                                                    <td>{email}</td>
                                                    <td className="text-end">
                                                        <Button variant="outline-danger" size="sm" className="rounded-pill py-0 px-2" onClick={() => handleUnassign(projectId, email)}>
                                                            <Trash2 size={12} className="me-1"/> Remove
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ));
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Tab>
                </Tabs>
            </Card.Body>
        </Card>
    );
};

export default BulkFacultyManager;